import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MapMeta, Task } from './types'
import { applyPrep, collectPrepIds, loadCore, loadItemNames, type CoreData } from './tarkovData'
import { mergeTaskVariants } from './taskVariants'
import { kappaChain } from './taskGraph'
import { useI18n } from './i18n'

/** 準備物（アイテム名）の取得ぐあい。16.7MB あるので遅延で取る */
export type PrepState = 'idle' | 'loading' | 'ready' | 'error'

/**
 * タスク・マップデータを読む。**ブラウザが json.tarkov.dev から直接取得**し、
 * IndexedDB に7日キャッシュする（→ doc/service/master.md#タスクデータの取得）。
 *
 * 更新頻度が低いので WebSocket には載せない。
 *
 * **表示言語ごとに別のデータ**。切り替えると取り直すが、キャッシュは言語別に残るので
 * 戻せば即出る（→ doc/i18n/master.md#キャッシュindexeddb）。
 */
export function useTasks() {
  const { lang } = useI18n()
  const [core, setCore] = useState<CoreData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [prepState, setPrepState] = useState<PrepState>('idle')
  /** 準備物の取得は1回だけ走らせる。言語が変われば引き直す */
  const prepRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    // 準備物のアイテム名も言語で変わる。取り直しの口を開けておく
    prepRef.current = false
    setPrepState('idle')
    // 取れるまでは前の言語の表示を残す（core は null に戻さない）
    loadCore(lang, (fresher) => {
      // 期限切れキャッシュで描き始めたあと、裏の取り直しが終わったら差し替える
      if (!cancelled) setCore(fresher)
    })
      .then((data) => {
        if (!cancelled) setCore(data)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [lang])

  /** 準備物パネルを開いたときに呼ぶ。アイテム名を取ってタスクに当てる */
  const requestPrep = useCallback(() => {
    if (prepRef.current || !core) return
    prepRef.current = true
    setPrepState('loading')
    loadItemNames(collectPrepIds(core.prepRaw), lang)
      .then((items) => {
        setCore((prev) => (prev ? { ...prev, tasks: applyPrep(prev.tasks, prev.prepRaw, items, lang) } : prev))
        setPrepState('ready')
      })
      .catch(() => {
        // 準備物だけが出ない。マップ・ピン・落書き・タスク一覧は通常どおり
        prepRef.current = false
        setPrepState('error')
      })
  }, [core, lang])

  /**
   * 中身が同じで ID だけ違うタスク（陣営違い・複製）は1件にまとめる。
   * 2人が別々に登録して同じ行・同じ図形が二重に出るのを防ぐ → src/taskVariants.ts
   */
  const merged = useMemo(() => mergeTaskVariants(core?.tasks ?? EMPTY_TASKS), [core])
  const tasks = merged.tasks

  /** 正のIDに加えて、**別版のIDからも同じタスクを引ける**（登録済みのボードが読める） */
  const byId = useMemo(() => {
    const map = new Map(tasks.map((t) => [t.id, t]))
    for (const [from, to] of merged.canonicalOf) {
      const task = map.get(to)
      if (task) map.set(from, task)
    }
    return map
  }, [tasks, merged])

  /**
   * カッパコンテナに要るタスク。フラグの立っているものから前提を辿って作る
   * （→ src/taskGraph.ts）。**別版に読み替えたIDでも引けるように正のID側で持つ。**
   */
  const kappaIds = useMemo(() => kappaChain(tasks), [tasks])

  return {
    tasks,
    kappaIds,
    /** 別版のID → 正のID。ボードに書く前にこれを通す */
    canonicalOf: merged.canonicalOf,
    traders: core?.traders ?? EMPTY_STRINGS,
    maps: core?.maps ?? EMPTY_STRINGS,
    mapMeta: core?.mapMeta ?? EMPTY_META,
    fetchedAt: core?.fetchedAt ?? null,
    available: tasks.length > 0,
    byId,
    loading,
    error,
    prepState,
    requestPrep,
  }
}

const EMPTY_TASKS: Task[] = []
const EMPTY_STRINGS: string[] = []
const EMPTY_META: Record<string, MapMeta> = {}
